// src/v2/lib/weekUtils.ts

import { getWeekStartsOn } from "@/shared/utils/firm";

// Canonical week starting on Sunday (date.getDay() order).
const WEEKDAYS_LETTER_SUN = ["S", "M", "T", "W", "T", "F", "S"] as const;
const WEEKDAYS_SHORT_SUN = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
const WEEKDAYS_FULL_SUN = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"] as const;

type WeekdayFormat = "letter" | "short" | "full";

function rotate<T>(arr: readonly T[], by: number): T[] {
	const n = arr.length;
	const offset = ((by % n) + n) % n;
	return [...arr.slice(offset), ...arr.slice(0, offset)];
}

/**
 * Weekday labels rotated to start on the given day (0 = Sun, 1 = Mon).
 * Defaults to the current firm's week-start setting.
 */
export function getWeekdayLabels(format: WeekdayFormat, weekStartsOn: 0 | 1 = getWeekStartsOn()): string[] {
	const source =
		format === "letter" ? WEEKDAYS_LETTER_SUN :
		format === "short" ? WEEKDAYS_SHORT_SUN :
		WEEKDAYS_FULL_SUN;
	return rotate(source, weekStartsOn);
}

/** Days from `weekStartsOn` to `date`. */
function weekOffset(date: Date, weekStartsOn: 0 | 1): number {
	return (date.getDay() - weekStartsOn + 7) % 7;
}

/**
 * Build a calendar grid starting on the given week-start day (defaults to firm setting).
 * Returns exactly `weeksNeeded * 7` Date objects covering the given month.
 */
export function buildCalendarGrid(
	year: number,
	month: number,
	weekStartsOn: 0 | 1 = getWeekStartsOn(),
): { days: Date[]; weeksNeeded: number } {
	const firstOfMonth = new Date(year, month, 1);
	const startOffset = weekOffset(firstOfMonth, weekStartsOn);
	const daysInMonth = new Date(year, month + 1, 0).getDate();
	const weeksNeeded = Math.ceil((startOffset + daysInMonth) / 7);

	const startDay = new Date(firstOfMonth);
	startDay.setDate(startDay.getDate() - startOffset);

	const days: Date[] = [];
	for (let i = 0; i < weeksNeeded * 7; i++) {
		const d = new Date(startDay);
		d.setDate(startDay.getDate() + i);
		days.push(d);
	}
	return { days, weeksNeeded };
}

/** Returns the first day of the week containing `date` (per firm setting by default). */
export function getWeekStart(date: Date, weekStartsOn: 0 | 1 = getWeekStartsOn()): Date {
	const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
	d.setDate(d.getDate() - weekOffset(d, weekStartsOn));
	return d;
}

/** Returns the 7 days starting on `weekStart`. */
export function getWeekDays(weekStart: Date): Date[] {
	const days: Date[] = [];
	for (let i = 0; i < 7; i++) {
		const d = new Date(weekStart);
		d.setDate(weekStart.getDate() + i);
		days.push(d);
	}
	return days;
}
